import { AdvancedMarker, Pin } from "@vis.gl/react-google-maps";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LANGUAGE,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_REGION,
} from "../../constants/google-maps-browser";
import { APIProvider } from "@vis.gl/react-google-maps";
import GenericMap from "./GenericMap";
import type { MapMouseEvent } from "@vis.gl/react-google-maps";
import { DEFAULT_CENTER } from "../../pages/mission-map/constants";

interface Geo {
  lat: number;
  lng: number;
}

interface MapPinDialogProps {
  open: boolean;
  title: string;
  initialValue?: Geo | null;
  onClose: () => void;
  onSave: (coords: Geo) => void;
}


/** Treat as valid only when lat/lng are present and non-zero. */
function isValidGeo(g: Geo | null | undefined): g is Geo {
  return !!(g && g.lat && g.lng);
}

const MapPinDialog: React.FC<MapPinDialogProps> = ({
  open,
  title,
  initialValue,
  onClose,
  onSave,
}) => {
  const [pin, setPin] = useState<Geo | null>(null);
  const theme = useTheme();

  const validInitial = isValidGeo(initialValue) ? initialValue : null;

  useEffect(() => {
    if (open) {
      setPin(validInitial ?? DEFAULT_CENTER);
    }
  }, [open, initialValue?.lat, initialValue?.lng]);

  const displayPin = pin ?? validInitial ?? DEFAULT_CENTER;
  // Uncontrolled mode so user can pan and zoom; initial view only.
  const initialCenter = validInitial ?? DEFAULT_CENTER;
  const initialZoom = validInitial ? 11 : 7;

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack gap={1}>
          <Typography variant="body2" color="text.secondary">
            לחץ/י על המפה כדי להציב סיכה.
          </Typography>
          <Box sx={{ height: 360, width: "100%" }}>
            <APIProvider
              apiKey={GOOGLE_MAPS_API_KEY}
              libraries={GOOGLE_MAPS_LIBRARIES}
              language={GOOGLE_MAPS_LANGUAGE}
              region={GOOGLE_MAPS_REGION}
            >
              <GenericMap
                defaultCenter={initialCenter}
                defaultZoom={initialZoom}
                minZoom={6}
                maxZoom={18}
                onClick={(event: MapMouseEvent) => {
                  const latLng = event.detail?.latLng;
                  if (latLng) setPin({ lat: latLng.lat, lng: latLng.lng });
                }}
                style={{ height: "100%", width: "100%" }}
              >
                <AdvancedMarker position={displayPin}>
                  <Pin
                    background={theme.palette.primary.main}
                    glyphColor={theme.palette.common.white}
                    borderColor={theme.palette.common.white}
                  />
                </AdvancedMarker>
              </GenericMap>
            </APIProvider>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          ביטול
        </Button>
        <Button
          onClick={() => {
            onSave(displayPin);
          }}
          variant="contained"
        >
          בחירה
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapPinDialog;
